import { Stack, styled, Typography } from '@mui/material'

import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { Chip } from '@/components/UI/Chip'
import { Copy } from '@/components/UI/Copy'
import { IBankConnectionDetail } from '@/types'
import { STATUS } from '@/utils/constants'
import { safeFormatDate } from '@/utils/common'
import { isInactiveConnectionStatus } from '@/utils/connectionStatus'

const statusChip = (value?: string | null) =>
  value?.trim() ? <Chip value={value} /> : <StyledText>N/A</StyledText>

// Drawn at the table's text size, and allowed to break anywhere so a long ID wraps inside its
// column instead of widening the table past the card.
const idTextStyle = {
  fontSize: '14px',
  lineHeight: '20px',
  wordBreak: 'break-all',
}

// Date first, as the KYC table reads its times.
const syncTime = (value?: string | null) => safeFormatDate(value ?? '', 'MMM D, YYYY hh:mm A')

const copyable = (value?: string | null) =>
  value?.trim() ? <Copy value={value} textStyle={idTextStyle} /> : <StyledText>N/A</StyledText>

export const connectionDetailColumns: ITableColumn<IBankConnectionDetail>[] = [
  {
    key: 'id',
    id: 'id',
    label: 'Connection ID',
    // Fixed widths lay the table out to the card's width, so nothing can push it wider.
    width: '26%',
    render: (_v, r) => copyable(r.id),
  },
  {
    key: 'status',
    id: 'status',
    label: 'Connection Status',
    width: '18%',
    render: (_v, r) => statusChip(r.status),
  },
  {
    key: 'lastSyncedAt',
    id: 'lastSyncedAt',
    label: 'Last Synced At',
    width: '20%',
    render: (_v, r) => <StyledText>{syncTime(r.lastSyncedAt)}</StyledText>,
  },
  {
    key: 'lastSyncedStatus',
    id: 'lastSyncedStatus',
    label: 'Sync Status',
    width: '16%',
    // An inactive connection no longer syncs, so its last sync status is stale and reads N/A. Done
    // here for now; the bank service can send it this way later. The reason is only worth showing
    // against a failed sync.
    render: (_v, r) =>
      isInactiveConnectionStatus(r.status) ? (
        <StyledText>N/A</StyledText>
      ) : (
        <Stack gap={0.5}>
          {statusChip(r.lastSyncedStatus)}
          {r.lastSyncedStatus === STATUS.FAILED && r.failureReason && (
            <StyledText>{r.failureReason}</StyledText>
          )}
        </Stack>
      ),
  },
  {
    key: 'lastSuccessfulSyncedAt',
    id: 'lastSuccessfulSyncedAt',
    // Differs from the last sync when that one failed. A connection never synced successfully reads
    // N/A.
    label: 'Last Successful Sync',
    width: '20%',
    render: (_v, r) => <StyledText>{syncTime(r.lastSuccessfulSyncedAt)}</StyledText>,
  },
]

const StyledText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: theme.palette.secondary.main,
  wordBreak: 'break-word',
}))
